import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import GroupsIcon from '@mui/icons-material/Groups'
import PersonIcon from '@mui/icons-material/Person'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import API from '../../../../services/API'
import {
  getApplicantLabel,
  groupShiftApplications,
  isSwapGroup,
} from '../../../admin/ShiftApplications/groupApplications'

// Pending applications for one event's shifts, grouped into the same review
// rows as the Shift Applications page. `refreshKey` changes whenever the
// event's own shift data shows a different set of pending applications, so
// the panel follows applications arriving or being reviewed elsewhere.
export const useEventShiftApplications = (eventID, refreshKey) => {
  const [applications, setApplications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = useCallback(() => {
    if (!eventID) return
    const params = new URLSearchParams({
      page: 0,
      pageSize: 100,
      shiftType: 'Event',
      event: eventID,
    })
    setError(false)
    API.get(`/shift-applications?${params.toString()}`)
      .then((res) => {
        if (res.status === 200) {
          setApplications(res.data?.applications ?? [])
        } else {
          setError(true)
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [eventID])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  const groups = useMemo(
    () => groupShiftApplications(applications),
    [applications],
  )

  // Every shift id a row covers (a merged vacancy spans several), so a
  // shift's applications icon can open its row.
  const groupByShiftID = useMemo(() => {
    const map = new Map()
    groups.forEach((group) =>
      group.applications.forEach((application) =>
        map.set(String(application.shift?._id), group),
      ),
    )
    return map
  }, [groups])

  return { groups, groupByShiftID, loading, error, reload: load }
}

const RowAvatar = ({ group }) => {
  const theme = useTheme()
  const single = group.applicantCount <= 1 && !group.alreadyFilled
  const tone = group.alreadyFilled
    ? theme.palette.warning.main
    : theme.palette.anBlue.main
  return (
    <Avatar
      src={
        single
          ? group.applications[0]?.employee?.userAccount?.photograph
          : undefined
      }
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1,
        bgcolor: alpha(tone, 0.12),
        color: tone,
      }}
    >
      {group.alreadyFilled ? (
        <WarningAmberIcon fontSize="small" />
      ) : single ? (
        <PersonIcon fontSize="small" />
      ) : (
        <GroupsIcon fontSize="small" />
      )}
    </Avatar>
  )
}

const smallChip = {
  borderRadius: 1,
  height: 20,
  fontSize: '0.68rem',
  fontWeight: 700,
}

const EventShiftApplications = ({ groups, loading, error, onOpen }) => {
  const theme = useTheme()
  const navigate = useNavigate()

  if (error) {
    return (
      <Alert severity="error" sx={{ py: 0 }}>
        Unable to load shift applications for this event.
      </Alert>
    )
  }
  if (loading || groups.length === 0) return null

  const applicantTotal = groups.reduce(
    (total, group) => total + group.applicantCount,
    0,
  )

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: alpha(theme.palette.anPurple.main, 0.25),
        borderRadius: '10px',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: alpha(theme.palette.anPurple.main, 0.06),
          borderLeft: '4px solid',
          borderLeftColor: 'anPurple.main',
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
          <Typography fontWeight={700} noWrap>
            Pending applications
          </Typography>
          <Chip
            size="small"
            color="anPurple"
            label={`${applicantTotal} applicant${applicantTotal === 1 ? '' : 's'}`}
            sx={smallChip}
          />
        </Stack>
        <Button
          size="small"
          onClick={() => navigate('/shift-applications')}
          sx={{ flexShrink: 0 }}
        >
          All applications
        </Button>
      </Stack>

      <Box component="ul" sx={{ m: 0, p: 0 }}>
        {groups.map((group) => (
          <Stack
            key={group.key}
            component="li"
            role="button"
            tabIndex={0}
            direction="row"
            alignItems="center"
            gap={1.25}
            onClick={() => onOpen(group)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onOpen(group)
              }
            }}
            sx={{
              listStyle: 'none',
              px: 1.5,
              py: 1,
              cursor: 'pointer',
              borderTop: '1px solid',
              borderColor: 'divider',
              '&:hover': { bgcolor: 'action.hover' },
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.anBlue.main}`,
                outlineOffset: -2,
              },
            }}
          >
            <RowAvatar group={group} />
            <Stack flex={1} minWidth={0} gap={0.25}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {getApplicantLabel(group)}
              </Typography>
              <Stack
                direction="row"
                gap={0.5}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="caption" color="text.secondary">
                  {moment(group.shift.start).format('ddd D MMM, HH:mm')}-
                  {moment(group.shift.end).format('HH:mm')}
                </Typography>
                {group.shift?.clinicalRole && (
                  <Chip
                    size="small"
                    label={group.shift.clinicalRole}
                    sx={smallChip}
                  />
                )}
                {group.shift?.eventAssignment && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={group.shift.eventAssignment}
                    sx={smallChip}
                  />
                )}
                {isSwapGroup(group) && (
                  <Chip
                    size="small"
                    color="anPurple"
                    icon={<SwapHorizIcon />}
                    label="Swap"
                    sx={smallChip}
                  />
                )}
                {group.alreadyFilled && (
                  <Chip
                    size="small"
                    color="warning"
                    label="Already filled"
                    sx={smallChip}
                  />
                )}
              </Stack>
            </Stack>
            <ChevronRightIcon color="action" fontSize="small" />
          </Stack>
        ))}
      </Box>
    </Box>
  )
}

export default EventShiftApplications
